import type { PackageSettings, GeneratedPackageSettings } from '@ms-cloudpack/common-types';
import { checkMatch } from './checkMatch.js';
import { mergePackageSettings } from './mergePackageSettings.js';
import { getBuiltInSettings } from '../builtInSettings/getBuiltInSettings.js';
import { getGeneratedPackageSettings } from './getGeneratedPackageSettings.js';

/** Result of calling `getPackageSettings`. */
export interface GetPackageSettingsResult {
  /** Merged user package settings, with `match` removed. */
  userPackageSettings: Omit<PackageSettings, 'match'> | undefined;
  /** Merged generated package settings, with `match` removed. */
  generatedPackageSettings: Omit<GeneratedPackageSettings, 'match'> | undefined;
}

/**
 * Gets the user and generated package settings which apply to this package name and version,
 * with the settings from each source merged together.
 *
 * NOTE: If you're calling from init-related code and would like to modify the result to update
 * the generated config, use `getGeneratedPackageSettings` instead.
 */
export function getPackageSettings(params: {
  name: string;
  version: string;
  /** User package settings from the config */
  userPackageSettings: PackageSettings[] | undefined;
  /** Generated package settings from the config */
  generatedPackageSettings: GeneratedPackageSettings[] | undefined;
  /** If true, merge built-in package settings with the user package settings. */
  includeBuiltIn?: boolean;
}): GetPackageSettingsResult {
  const { name, version, userPackageSettings, generatedPackageSettings, includeBuiltIn } = params;

  const matchingUserSettings = userPackageSettings?.filter(({ match }) => checkMatch({ name, version, match })) || [];

  if (includeBuiltIn) {
    const builtInSettings = getBuiltInSettings({ name, version });
    if (builtInSettings?.packageSettings) {
      // Put the built-in settings first so they can be overridden by user settings
      matchingUserSettings.unshift({ match: { name, version }, ...builtInSettings.packageSettings });
    }
  }

  // For generated settings, restrict to exact matches since the name won't have wildcards
  const matchingGeneratedSettings = getGeneratedPackageSettings({
    name,
    version,
    generatedPackageSettings: generatedPackageSettings || [],
  });

  return {
    userPackageSettings: matchingUserSettings.length ? mergePackageSettings(matchingUserSettings) : undefined,
    // Even with a single generated setting, we use mergePackageSettings to remove the 'match' property from the result
    generatedPackageSettings: matchingGeneratedSettings ? mergePackageSettings([matchingGeneratedSettings]) : undefined,
  };
}
